// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/ops.
// Suppressions: search, filter by scope and reason, add (admins), and remove with confirmation.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { MailX, Plus, Search, Trash2, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { allApplicationsQuery } from '@/api/queries/applications'
import type { Suppression, SuppressionReason } from '@/api/types'
import { Can } from '@/auth/Can'
import { usePermissions } from '@/auth/usePermissions'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { DataTable, tableColumns } from '@/components/DataTable'
import { EmptyState } from '@/components/EmptyState'
import { PageHeader } from '@/components/PageHeader'
import { RelativeTime } from '@/components/RelativeTime'
import { Badge } from '@/components/ui/badge'
import { Button, IconButton } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { AddSuppressionDialog } from '@/features/ops/AddSuppressionDialog'
import { useDeleteSuppression, useSuppressions } from '@/features/ops/api'
import { REASONS } from '@/features/ops/suppression-reasons'
import { useDebouncedValue } from '@/lib/useDebouncedValue'
import { useAppScope } from '@/shell/useAppScope'

/** `scope`: 'global' for global-only, or 'all' to override the app scope. Default follows the app scope. */
interface Search {
  q?: string
  scope?: 'global' | 'all'
  reason?: SuppressionReason
  expired?: boolean
}

const isReason = (v: unknown): v is SuppressionReason => typeof v === 'string' && v in REASONS

export const Route = createFileRoute('/suppressions')({
  staticData: { crumb: 'Suppressions' },
  validateSearch: (s: Record<string, unknown>): Search => ({
    q: typeof s.q === 'string' && s.q ? s.q : undefined,
    scope: s.scope === 'global' || s.scope === 'all' ? s.scope : undefined,
    reason: isReason(s.reason) ? s.reason : undefined,
    expired: s.expired === true || s.expired === 'true' ? true : undefined,
  }),
  component: SuppressionsPage,
})

const REASON_TONE: Record<SuppressionReason, 'danger' | 'warning' | 'neutral' | 'info'> = {
  hard_bounce: 'danger',
  complaint: 'warning',
  unsubscribe: 'info',
  manual: 'neutral',
}

type Row = Suppression & { appName?: string }

const col = tableColumns<Row>()

function buildColumns(onRemove: (s: Row) => void, canManage: boolean) {
  return col.columns([
    col.accessor('email', {
      header: 'Email',
      cell: (c) => (
        <span className="font-mono text-[13px] whitespace-nowrap text-ink">{c.getValue()}</span>
      ),
      enableHiding: false,
    }),
    col.accessor((s) => s.appName ?? '', {
      id: 'scope',
      header: 'Scope',
      cell: (c) =>
        c.row.original.application_id ? (
          <span className="truncate">
            {c.row.original.appName ?? c.row.original.application_id}
          </span>
        ) : (
          <Badge tone="outline">Global</Badge>
        ),
      meta: { width: '11rem' },
    }),
    col.accessor('reason', {
      header: 'Reason',
      cell: (c) => <Badge tone={REASON_TONE[c.getValue()]}>{REASONS[c.getValue()].label}</Badge>,
      meta: { width: '8.5rem' },
    }),
    col.accessor('detail', {
      header: 'Detail',
      cell: (c) =>
        c.getValue() ? (
          <span className="line-clamp-2 text-ink-2" title={c.getValue()}>
            {c.getValue()}
          </span>
        ) : (
          <span className="text-ink-3">—</span>
        ),
      enableSorting: false,
    }),
    col.accessor('expires_at', {
      header: 'Expires',
      cell: (c) => {
        const v = c.getValue()
        if (!v) return <span className="text-ink-3">Never</span>
        const lapsed = new Date(v).getTime() <= Date.now()
        return (
          <RelativeTime value={v} className={lapsed ? 'text-ink-3 line-through' : 'text-ink-2'} />
        )
      },
      sortFn: 'datetime',
      meta: { width: '8rem' },
    }),
    col.accessor('created_at', {
      header: 'Added',
      cell: (c) => (
        <div>
          <RelativeTime value={c.getValue()} className="text-ink-2" />
          <div className="truncate text-xs text-ink-3">by {c.row.original.created_by}</div>
        </div>
      ),
      sortFn: 'datetime',
      meta: { width: '9rem' },
    }),
    col.display({
      id: 'actions',
      header: () => <span className="sr-only">Actions</span>,
      cell: (c) =>
        canManage ? (
          <IconButton
            label={`Remove ${c.row.original.email}`}
            size="sm"
            variant="danger-ghost"
            onClick={() => onRemove(c.row.original)}
          >
            <Trash2 />
          </IconButton>
        ) : null,
      enableHiding: false,
      meta: { align: 'right', width: '3.5rem', label: '' },
    }),
  ])
}

function SuppressionsPage() {
  const search = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const { appId, application } = useAppScope()
  const { can } = usePermissions()
  const canManage = can('manage_config')
  const [adding, setAdding] = useState(false)
  const [removing, setRemoving] = useState<Row | undefined>()
  const [text, setText] = useState(search.q ?? '')
  const q = useDebouncedValue(text.trim().toLowerCase(), 250)
  const del = useDeleteSuppression()
  const apps = useQuery(allApplicationsQuery)

  // Mirror the search box into the URL so the filtered list is shareable.
  const settled = q === text.trim().toLowerCase()
  useEffect(() => {
    if (settled && (search.q ?? '') !== q)
      void navigate({ search: (prev) => ({ ...prev, q: q || undefined }), replace: true })
  }, [settled, search.q, q, navigate])

  // Scope: explicit 'global'/'all' wins; otherwise follow the global application scope.
  const scope = search.scope ?? (appId ? 'app' : 'all')
  const list = useSuppressions({
    app: scope === 'global' ? 'global' : scope === 'app' ? appId : undefined,
    q: q || undefined,
    reason: search.reason,
    includeExpired: search.expired,
  })

  const names = useMemo(() => new Map((apps.data ?? []).map((a) => [a.id, a.name])), [apps.data])
  const rows = useMemo<Row[]>(
    () =>
      list.items.map((s) => ({
        ...s,
        appName: s.application_id ? names.get(s.application_id) : undefined,
      })),
    [list.items, names],
  )
  const columns = useMemo(() => buildColumns(setRemoving, canManage), [canManage])

  const set = (patch: Partial<Search>) =>
    void navigate({ search: (prev) => ({ ...prev, ...patch }), replace: true })

  const scopeOptions = [
    ...(appId ? [{ value: 'app', label: `${application?.name ?? 'This application'} only` }] : []),
    { value: 'all', label: appId ? 'Every scope' : 'All scopes' },
    { value: 'global', label: 'Global only' },
  ]
  const filtered = !!(q || search.reason || search.scope)

  return (
    <>
      <PageHeader
        title="Suppressions"
        description="Addresses MailSmith won’t send to, globally or for one application. Hard bounces and complaints are added automatically."
        actions={
          <Can permission="manage_config" mode="disable">
            <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
              Add suppression
            </Button>
          </Can>
        }
      />
      <DataTable
        aria-label="Suppressions"
        columns={columns}
        data={rows}
        getRowId={(s) => s.id}
        isLoading={list.isPending}
        error={list.error}
        onRetry={() => void list.refetch()}
        pagination={list.pagination}
        persistKey="suppressions"
        toolbar={
          <>
            <Input
              className="w-64"
              size="sm"
              placeholder="Email starts with…"
              aria-label="Search by email"
              value={text}
              onChange={(e) => setText(e.target.value)}
              leading={<Search />}
              trailing={
                text ? (
                  <IconButton
                    label="Clear search"
                    size="xs"
                    tooltip={false}
                    onClick={() => setText('')}
                  >
                    <X />
                  </IconButton>
                ) : undefined
              }
            />
            <Select
              aria-label="Scope"
              size="sm"
              className="w-48"
              value={scope}
              onValueChange={(v) =>
                set({
                  scope:
                    v === 'app' || (v === 'all' && !appId) ? undefined : (v as 'global' | 'all'),
                })
              }
              options={scopeOptions}
            />
            <Select
              aria-label="Reason"
              size="sm"
              className="w-40"
              value={search.reason ?? 'any'}
              onValueChange={(v) =>
                set({ reason: v === 'any' ? undefined : (v as SuppressionReason) })
              }
              options={[
                { value: 'any', label: 'Any reason' },
                ...Object.entries(REASONS).map(([value, r]) => ({ value, label: r.label })),
              ]}
            />
            <div className="flex items-center gap-2">
              <Switch
                id="include-expired"
                checked={!!search.expired}
                onCheckedChange={(v) => set({ expired: v || undefined })}
              />
              <Label htmlFor="include-expired" className="font-normal text-ink-2">
                Include expired
              </Label>
            </div>
          </>
        }
        empty={
          filtered ? (
            <EmptyState
              compact
              icon={Search}
              title="No matching suppressions"
              description="Nothing matches these filters. Addresses not listed here can be emailed."
              action={
                <Button
                  onClick={() => {
                    setText('')
                    set({ q: undefined, reason: undefined, scope: undefined })
                  }}
                >
                  Clear filters
                </Button>
              }
            />
          ) : (
            <EmptyState
              icon={MailX}
              title="No suppressed addresses"
              description="Hard bounces and spam complaints land here automatically. Add an address by hand when someone asks not to be emailed."
              action={
                canManage ? (
                  <Button variant="primary" icon={<Plus />} onClick={() => setAdding(true)}>
                    Add suppression
                  </Button>
                ) : undefined
              }
            />
          )
        }
      />
      <AddSuppressionDialog open={adding} onOpenChange={setAdding} defaultAppId={appId} />
      <ConfirmDialog
        open={!!removing}
        onOpenChange={(o) => !o && setRemoving(undefined)}
        title={`Remove ${removing?.email ?? 'suppression'}?`}
        description={
          removing?.reason === 'hard_bounce' || removing?.reason === 'complaint'
            ? `This address was suppressed after a ${REASONS[removing.reason].label.toLowerCase()}. Sending to it again can hurt the sending reputation for every application.`
            : 'MailSmith will send to this address again.'
        }
        confirmLabel="Remove suppression"
        destructive
        onConfirm={() => removing && del.mutateAsync(removing.id)}
      />
    </>
  )
}
